/* Admin Backup Center + Security Scanner config + Upload Security Log.
   Three tabs in one page for fast access from the Admin group of the sidebar. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const COLLECTION_LABELS = {
  customers: "Customers / leads",
  vehicles: "Vehicles + vAuto staging",
  deals: "Deals (sold leads)",
  trade_appraisals: "Trade appraisals",
  tasks: "Tasks",
  appointments: "Appointments",
  notes: "Notes",
  messages: "Messages / conversations",
  deal_jacket: "Deal jacket records",
  uploaded_metadata: "Uploaded file metadata",
  audit_logs: "Audit logs",
  users: "Users & roles",
  settings: "Settings / configuration",
  templates: "Templates",
  field_mappings: "PDF field mappings",
};

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0)} ${u[i]}`;
};

export default function AdminBackupCenter() {
  const [tab, setTab] = useState("backup");
  return (
    <div className="bg-slate-50 min-h-full" data-testid="page-backup-center">
      <div className="bg-gradient-to-b from-[#061426] to-[#0a1f3a] text-white border-b border-white/10">
        <div className="px-5 py-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-bold">System</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold mt-0.5">Security &amp; Backup Center</h1>
          <p className="text-sm text-blue-200/70 mt-1">Malware scanner configuration, upload security log, and CRM backup exports.</p>
          <div className="mt-3 flex gap-2">
            <Link to="/admin/today" className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-white/10 text-white/80 hover:bg-white/20">← Dashboard</Link>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex flex-wrap gap-1 border-b border-gray-200">
          {[
            { k: "backup", label: "Backup Center" },
            { k: "scanner", label: "Malware Scanner" },
            { k: "log", label: "Upload Security Log" },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`text-sm px-4 py-2 border-b-2 ${tab === t.k ? "border-[#006DFF] text-[#006DFF] font-semibold" : "border-transparent text-gray-600 hover:text-[#061426]"}`}
              data-testid={`bcenter-tab-${t.k}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "backup" && <BackupTab />}
        {tab === "scanner" && <ScannerTab />}
        {tab === "log" && <UploadLogTab />}
      </div>
    </div>
  );
}

/* =====================================================================
   Backup tab
   ===================================================================== */
function BackupTab() {
  const [opts, setOpts] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(() => Object.keys(COLLECTION_LABELS));
  const [fmt, setFmt] = useState("json");
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const a = await api.get("/admin/backup/options");
      setOpts(a.data);
      const b = await api.get("/admin/backup/history?limit=20");
      setHistory(b.data.items || []);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (k) => setPicked(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  const fire = async (kind) => {
    if (!window.confirm(`Run ${kind} backup now? You can download it afterwards from the history table below.`)) return;
    setBusy(true); setError("");
    try {
      if (kind === "mongo_dump") {
        await api.post("/admin/backup/mongo-dump", { note });
      } else if (kind === "readable") {
        if (picked.length === 0) { setError("Pick at least one collection."); setBusy(false); return; }
        await api.post("/admin/backup/readable-export", { collection_keys: picked, fmt, note });
      } else if (kind === "files") {
        await api.post("/admin/backup/files", { note });
      }
      await load();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const downloadUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/download/${id}`;
  const downloadWithAuth = async (h) => {
    try {
      const r = await api.get(`/admin/backup/download/${h.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = h.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this backup from disk + history?")) return;
    try { await api.delete(`/admin/backup/${id}`); await load(); }
    catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  if (!opts) return <p className="text-gray-500 text-sm">Loading…</p>;

  const inputCls = "w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-4" data-testid="backup-tab">
      {error && <div className="bg-red-50 border border-red-300 text-red-800 text-sm rounded p-3" data-testid="backup-error">{error}</div>}

      {/* What gets backed up */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="backup-coverage">
        <div className="font-bold mb-1">What's covered by each backup type</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
          <Pill label="Mongo dump" tone="emerald">Full DB · disaster recovery · binary `.gz` (use mongorestore)</Pill>
          <Pill label="Readable export" tone="blue">JSON or CSV per collection · human-readable · inspection / reporting</Pill>
          <Pill label="File archive" tone="purple">tar.gz of {opts.uploads_folder} ({opts.uploads_file_count} files, {fmtBytes(opts.uploads_size_bytes)})</Pill>
        </div>
        <div className="mt-2 text-[11px]">
          {opts.mongodump_available
            ? "✓ mongodump is available in this environment."
            : "⚠ mongodump is NOT available — Mongo dump button will fail. Use Readable export until your env supports it."}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Mongo dump card */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Disaster Recovery</div>
          <h3 className="text-sm font-bold text-[#061426] mb-2">1. Full Mongo dump</h3>
          <p className="text-xs text-gray-600 mb-3">Binary archive of every collection. Restorable with <code>mongorestore --gzip --archive</code>.</p>
          <button onClick={() => fire("mongo_dump")} disabled={busy || !opts.mongodump_available}
            className="w-full bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-bold px-3 py-2 rounded"
            data-testid="backup-run-mongo">
            {busy ? "Working…" : "Run Mongo dump now"}
          </button>
        </div>

        {/* Readable export card */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700 mb-1">Business Records</div>
          <h3 className="text-sm font-bold text-[#061426] mb-2">2. Readable export</h3>
          <p className="text-xs text-gray-600 mb-2">Pick the collections + format, then download.</p>
          <div className="max-h-44 overflow-y-auto border border-gray-200 rounded p-2 mb-2">
            {Object.keys(COLLECTION_LABELS).map(k => (
              <label key={k} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer hover:bg-blue-50 rounded px-1">
                <input type="checkbox" checked={picked.includes(k)} onChange={() => toggle(k)} data-testid={`backup-col-${k}`} />
                <span className="flex-1">{COLLECTION_LABELS[k]}</span>
                <span className="text-[10px] text-gray-400">{opts.collection_stats[k]?.approx_count ?? 0}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 mb-2">
            <select value={fmt} onChange={e => setFmt(e.target.value)} className={inputCls + " w-28"} data-testid="backup-fmt">
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
            </select>
            <button onClick={() => setPicked(Object.keys(COLLECTION_LABELS))} className="text-xs px-2 py-1 rounded border border-gray-300">All</button>
            <button onClick={() => setPicked([])} className="text-xs px-2 py-1 rounded border border-gray-300">None</button>
          </div>
          <button onClick={() => fire("readable")} disabled={busy}
            className="w-full bg-[#006DFF] hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-bold px-3 py-2 rounded"
            data-testid="backup-run-readable">
            {busy ? "Working…" : `Export ${picked.length} collection(s)`}
          </button>
        </div>

        {/* File archive card */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider font-bold text-purple-700 mb-1">Uploaded Files</div>
          <h3 className="text-sm font-bold text-[#061426] mb-2">3. File archive</h3>
          <p className="text-xs text-gray-600 mb-2">tar.gz of <code className="text-[11px]">{opts.uploads_folder}</code> — the actual binaries (not just metadata).</p>
          <div className="text-xs text-gray-500 mb-3">
            {opts.uploads_file_count} files · {fmtBytes(opts.uploads_size_bytes)}
          </div>
          <button onClick={() => fire("files")} disabled={busy}
            className="w-full bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-white text-xs font-bold px-3 py-2 rounded"
            data-testid="backup-run-files">
            {busy ? "Working…" : "Archive uploaded files"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
        <label className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">Backup note (optional)</label>
        <input value={note} onChange={e => setNote(e.target.value)} className={inputCls + " mt-1"} placeholder="e.g. before vAuto cutover" data-testid="backup-note" />
      </div>

      {/* History */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#061426]">Backup history</h3>
          <button onClick={load} className="text-xs text-blue-700 hover:text-blue-900 font-semibold">↻ Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="backup-history">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">By</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Size</th>
                <th className="text-left px-3 py-2">Includes</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id} className="border-t border-gray-100 hover:bg-blue-50/40">
                  <td className="px-3 py-2 text-xs"><BackupTypeBadge type={h.type} /></td>
                  <td className="px-3 py-2 text-xs text-gray-600">{(h.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-xs">{h.created_by}</td>
                  <td className="px-3 py-2 text-xs"><StatusBadge status={h.status} /></td>
                  <td className="px-3 py-2 text-xs text-right font-mono">{fmtBytes(h.size_bytes)}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 max-w-[18rem] truncate">{(h.includes || []).join(", ")}</td>
                  <td className="px-3 py-2 text-right">
                    {h.file_present && h.status === "completed" && (
                      <button onClick={() => downloadWithAuth(h)} className="bg-[#061426] hover:bg-[#0a1f3a] text-white text-[10px] font-bold px-2 py-1 rounded mr-1" data-testid={`backup-download-${h.id}`}>Download</button>
                    )}
                    <button onClick={() => remove(h.id)} className="text-[10px] text-red-600 hover:underline" data-testid={`backup-delete-${h.id}`}>Delete</button>
                  </td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-gray-400">No backups yet — run one above.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <details className="bg-white rounded-lg border border-gray-200 p-3 text-xs">
        <summary className="font-bold text-[#061426] cursor-pointer">Restore instructions</summary>
        <div className="mt-2 space-y-3 text-gray-700">
          <div>
            <div className="font-bold">Mongo dump (`.gz`)</div>
            <pre className="bg-slate-50 p-2 rounded font-mono text-[11px] overflow-x-auto">
mongorestore --uri=$MONGO_URL --gzip --archive=mongo_dump_XXXXXX.gz
            </pre>
            <p>Add <code>--drop</code> to wipe and replace, or omit to merge.</p>
          </div>
          <div>
            <div className="font-bold">Readable JSON (`.json.gz`)</div>
            <p>Unzip with <code>gunzip</code>, then open in any code editor or <code>jq</code>. For inspection / reporting only.</p>
          </div>
          <div>
            <div className="font-bold">Readable CSV (`.tar.gz`)</div>
            <p>Extract — one CSV per collection. Open in Excel / Numbers / Google Sheets.</p>
          </div>
          <div>
            <div className="font-bold">File archive (`crm_files_*.tar.gz`)</div>
            <pre className="bg-slate-50 p-2 rounded font-mono text-[11px] overflow-x-auto">
tar -xzf crm_files_XXXXXX.tar.gz -C {opts.uploads_folder}
            </pre>
            <p>Pair with a Mongo dump from the same timestamp so file metadata + binaries line up.</p>
          </div>
        </div>
      </details>
    </div>
  );
}

/* =====================================================================
   Scanner tab
   ===================================================================== */
function ScannerTab() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [probe, setProbe] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/security/scanner");
      setCfg(r.data); setDraft({
        enabled: r.data.config.enabled,
        scan_url: r.data.config.scan_url,
        notes: r.data.config.notes,
        block_on_skipped: r.data.config.block_on_skipped,
      });
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const body = { ...draft };
      if (draft.scan_token) body.scan_token = draft.scan_token;
      await api.patch("/admin/security/scanner", body);
      await load();
      setDraft(d => ({ ...d, scan_token: "" }));
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const runProbe = async () => {
    setProbe(null); setError("");
    try { const r = await api.post("/admin/security/scanner/test"); setProbe(r.data); }
    catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  if (!cfg) return <p className="text-gray-500 text-sm">Loading…</p>;
  const inputCls = "w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-4 max-w-3xl" data-testid="scanner-tab">
      {error && <div className="bg-red-50 border border-red-300 text-red-800 text-sm rounded p-3" data-testid="scanner-error">{error}</div>}

      <div className={`rounded-lg border p-3 text-sm ${cfg.scanner_active ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900"}`} data-testid="scanner-status">
        <div className="font-bold uppercase tracking-wider text-[10px]">Status</div>
        {cfg.scanner_active
          ? "✓ Scanner active — every upload is sent to the configured URL."
          : "⚠ No scanner configured. Magic-byte + MIME validation + size cap still protect uploads, but no external malware lookup happens."}
        {cfg.env_override.MALWARE_SCAN_URL_set && (
          <div className="text-[11px] mt-1 opacity-80">Env override active: <code>MALWARE_SCAN_URL</code> set to <code>{cfg.env_override.url_preview}</code>. Env wins over UI config.</div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <h3 className="text-sm font-bold text-[#061426] mb-3">Generic scanner hook</h3>
        <p className="text-xs text-gray-600 mb-3">
          Provide any HTTPS endpoint that accepts <code>POST</code> with JSON <code>{`{sha256, size, mime, filename}`}</code> and returns
          <code>{`{verdict: "clean"|"suspicious"|"malicious", detail?: str}`}</code>.
          Works with VirusTotal, MetaDefender, or a custom shim — your call.
        </p>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={!!draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} data-testid="scanner-enabled" />
          Enable external scanning
        </label>
        <label className="block mb-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Scanner URL</div>
          <input value={draft.scan_url || ""} onChange={e => setDraft({ ...draft, scan_url: e.target.value })} className={inputCls} placeholder="https://your-scanner.example.com/scan" data-testid="scanner-url" />
        </label>
        <label className="block mb-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">
            Bearer token (sent as Authorization header) {cfg.config.scan_token_mask && <span className="text-[10px] text-gray-400 ml-2">stored: {cfg.config.scan_token_mask}</span>}
          </div>
          <input type="password" value={draft.scan_token || ""} onChange={e => setDraft({ ...draft, scan_token: e.target.value })} className={inputCls + " font-mono"} placeholder="(leave blank to keep existing)" data-testid="scanner-token" />
        </label>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={!!draft.block_on_skipped} onChange={e => setDraft({ ...draft, block_on_skipped: e.target.checked })} />
          Treat "skipped" verdicts as rejection (strictest mode — recommended only after the scanner is proven stable)
        </label>
        <label className="block mb-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Notes</div>
          <textarea value={draft.notes || ""} onChange={e => setDraft({ ...draft, notes: e.target.value })} rows={2} className={inputCls} />
        </label>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="bg-[#061426] hover:bg-[#0a1f3a] disabled:opacity-50 text-white text-xs font-bold px-4 py-2 rounded" data-testid="scanner-save">{saving ? "Saving…" : "Save scanner config"}</button>
          <button onClick={runProbe} className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-4 py-2 rounded" data-testid="scanner-probe">Send test probe</button>
        </div>
        {probe && (
          <div className={`mt-3 text-xs rounded p-2 border ${probe.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`} data-testid="scanner-probe-result">
            <div className="font-bold">Probe result</div>
            <div>HTTP: {probe.status ?? "—"} · verdict: {probe.verdict ?? "(no verdict field)"} {probe.detail && `· ${probe.detail}`}</div>
            {probe.body_preview && <pre className="font-mono text-[10px] mt-1 max-h-20 overflow-y-auto">{probe.body_preview}</pre>}
          </div>
        )}
      </div>
    </div>
  );
}

/* =====================================================================
   Upload security log tab
   ===================================================================== */
function UploadLogTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (filter === "blocked") params.blocked_only = true;
      else if (filter === "accepted") params.accepted = true;
      if (q) params.q = q;
      const r = await api.get("/admin/security/uploads", { params });
      setItems(r.data.items || []);
      const s = await api.get("/admin/security/uploads/stats");
      setStats(s.data);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="space-y-3" data-testid="log-tab">
      {error && <div className="bg-red-50 border border-red-300 text-red-800 text-sm rounded p-3">{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="log-stats">
          <Stat label="Last 30d" value={stats.total} />
          <Stat label="Accepted" value={stats.accepted} accent="emerald" />
          <Stat label="Blocked" value={stats.blocked} accent={stats.blocked ? "red" : null} />
          <Stat label="Clean (scanned)" value={stats.clean} accent="emerald" />
          <Stat label="Suspicious" value={stats.suspicious} accent="amber" />
          <Stat label="Malicious" value={stats.malicious} accent={stats.malicious ? "red" : null} />
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm flex flex-wrap gap-2 items-center">
        <div className="flex gap-1">
          {[{ k: "all", l: "All" }, { k: "accepted", l: "Accepted" }, { k: "blocked", l: "Blocked" }].map(t => (
            <button key={t.k} onClick={() => setFilter(t.k)}
              className={`text-xs px-3 py-1.5 rounded font-semibold ${filter === t.k ? "bg-[#061426] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              data-testid={`log-filter-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder="Search filename / user / sha / reason…"
          className="border border-gray-300 rounded px-3 py-1.5 text-xs flex-1 min-w-[12rem]" data-testid="log-search" />
        <button onClick={load} className="text-xs font-semibold text-blue-700 hover:text-blue-900">↻ Refresh</button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="log-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">By</th>
              <th className="text-left px-3 py-2">Kind</th>
              <th className="text-left px-3 py-2">Filename</th>
              <th className="text-right px-3 py-2">Size</th>
              <th className="text-left px-3 py-2">MIME</th>
              <th className="text-left px-3 py-2">Verdict</th>
              <th className="text-left px-3 py-2">Result</th>
            </tr>
          </thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id} className="border-t border-gray-100 hover:bg-blue-50/40">
                <td className="px-3 py-2 text-xs text-gray-600 font-mono">{(i.at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 text-xs">{i.uploaded_by_name}</td>
                <td className="px-3 py-2 text-xs">{i.file_kind}</td>
                <td className="px-3 py-2 text-xs truncate max-w-[14rem]" title={i.filename}>{i.filename || "—"}</td>
                <td className="px-3 py-2 text-xs text-right font-mono">{fmtBytes(i.size_bytes)}</td>
                <td className="px-3 py-2 text-[10px] font-mono text-gray-500">{i.true_mime || i.declared_mime || "—"}</td>
                <td className="px-3 py-2 text-xs"><VerdictBadge v={i.scan_verdict} /></td>
                <td className="px-3 py-2 text-xs">
                  {i.accepted ? <span className="text-emerald-700 font-bold">✓ ACCEPTED</span>
                              : <span className="text-red-700 font-bold">✕ BLOCKED — {i.blocked_reason || "?"}</span>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-gray-400">No upload events match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----- shared small UI helpers ----- */
function StatusBadge({ status }) {
  const c = status === "completed" ? "bg-emerald-100 text-emerald-700" :
            status === "failed" ? "bg-red-100 text-red-700" :
            status === "running" ? "bg-blue-100 text-blue-700" :
            "bg-gray-100 text-gray-700";
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${c}`}>{status || "?"}</span>;
}
function BackupTypeBadge({ type }) {
  const m = { mongo_dump: ["Mongo dump", "bg-emerald-100 text-emerald-700"],
              readable_export_json: ["JSON export", "bg-blue-100 text-blue-700"],
              readable_export_csv: ["CSV export", "bg-blue-100 text-blue-700"],
              file_archive: ["File archive", "bg-purple-100 text-purple-700"] };
  const [label, cls] = m[type] || [type, "bg-gray-100 text-gray-700"];
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${cls}`}>{label}</span>;
}
function VerdictBadge({ v }) {
  if (!v) return <span className="text-xs text-gray-400">—</span>;
  const c = v === "clean" ? "bg-emerald-100 text-emerald-700" :
            v === "suspicious" || v === "rejected" ? "bg-amber-100 text-amber-700" :
            v === "malicious" ? "bg-red-100 text-red-700" :
            "bg-gray-100 text-gray-600";
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${c}`}>{v}</span>;
}
function Stat({ label, value, accent }) {
  const c = accent === "emerald" ? "text-emerald-600" :
            accent === "amber" ? "text-amber-600" :
            accent === "red" ? "text-red-600" : "text-[#061426]";
  return (
    <div className="bg-white border border-gray-200 rounded p-2 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-xl font-bold ${c}`}>{value ?? 0}</div>
    </div>
  );
}
function Pill({ label, tone, children }) {
  const c = tone === "emerald" ? "bg-emerald-100 text-emerald-900 border-emerald-200" :
            tone === "blue" ? "bg-blue-100 text-blue-900 border-blue-200" :
            tone === "purple" ? "bg-purple-100 text-purple-900 border-purple-200" :
            "bg-gray-100 text-gray-800 border-gray-200";
  return (
    <div className={`border rounded p-2 ${c}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider">{label}</div>
      <div className="text-[11px]">{children}</div>
    </div>
  );
}
